import type React from 'react';

/**
 * Navigation helper utilities for consistent scroll-to-top behavior and smooth transitions
 */

/**
 * Whether a click on a link should be left to the browser rather than handled
 * by the router.
 *
 * react-router's `Link` has this logic built in; this exists for the one place
 * that cannot use `Link` -- the animated CTA, which renders its own anchor so it
 * can keep the ripple. Without it, cmd-click and middle-click on those buttons
 * navigate the current tab instead of opening a new one, which is what happened
 * for every header item while the navigation was made of `<button>` elements.
 */
export const clickBelongsToBrowser = (event: React.MouseEvent): boolean =>
  event.defaultPrevented ||
  event.button !== 0 ||
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey;

/**
 * Navigates to `href` and puts the page back at the top.
 *
 * NO LOADING STATE, NO DELAY. Every route is in the main bundle — there is no
 * lazy chunk to wait for — so the render is instant. The old version fired the
 * full-screen loading overlay and then navigated 150ms later, which read as the
 * page flashing/reloading on every menu click. `navigationStarted` is still
 * accepted so call sites need not change; it is simply not used.
 *
 * @param navigate - React Router navigate function
 * @param href - The destination path to navigate to
 */
export const scrollToTopAndNavigate = (
  navigate: (path: string) => void,
  href: string,
  _navigationStarted?: (destination?: string) => void,
  _delay?: number
): void => {
  window.scrollTo(0, 0);
  navigate(href);
};

/**
 * Instantly scrolls to the top of the page (no animation)
 * Useful for immediate scroll-to-top on route changes
 */
export const scrollToTopInstant = (): void => {
  window.scrollTo(0, 0);
};

/**
 * Smoothly scrolls to the top of the page
 * Useful for scroll-to-top buttons or similar interactions
 */
export const scrollToTopSmooth = (): void => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

/**
 * Creates an enhanced navigation handler with loading state and smooth transitions
 * Returns a function that can be used as an onClick handler
 *
 * @param navigate - React Router navigate function
 * @param href - The destination path to navigate to
 * @param navigationStarted - Optional callback to trigger loading state
 * @param scrollDelay - Optional delay in ms before navigation (default: 150ms)
 * @returns Click handler function
 */
export const createScrollToTopNavHandler = (
  navigate: (path: string) => void,
  href: string,
  navigationStarted?: (destination?: string) => void,
  scrollDelay: number = 150
) => {
  return () => scrollToTopAndNavigate(navigate, href, navigationStarted, scrollDelay);
};

/**
 * Button navigation: a quick press animation on the clicked element, then the
 * same instant navigation as everything else. See `scrollToTopAndNavigate` for
 * why there is no loading overlay.
 *
 * @param navigate - React Router navigate function
 * @param href - The destination path to navigate to
 * @param element - Optional button element for micro-interactions
 */
export const animatedNavigate = (
  navigate: (path: string) => void,
  href: string,
  _navigationStarted?: (destination?: string) => void,
  element?: HTMLElement
): void => {
  if (element) {
    element.style.transform = 'scale(0.95)';
    element.style.transition = 'transform 0.05s ease-out';
    setTimeout(() => {
      element.style.transform = 'scale(1)';
    }, 50);
  }

  window.scrollTo(0, 0);
  navigate(href);
};

/**
 * Enhanced navigation for Contact page with scroll-to-top behavior
 * All Contact page navigation now goes to the top of the page by default
 *
 * @param navigate - React Router navigate function
 * @param currentPath - Current location pathname
 * @param navigationStarted - Optional callback to trigger loading state
 */
export const navigateToContactForm = (
  navigate: (path: string) => void,
  currentPath: string,
  navigationStarted?: (destination?: string) => void
): void => {
  if (currentPath === '/contact') {
    // Already on contact page, scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } else {
    // Navigate to contact page and scroll to top (no auto-scroll to form)
    scrollToTopAndNavigate(navigate, '/contact', navigationStarted);
  }
};

/**
 * Standard Contact page navigation that always goes to the top
 * Use this for all buttons that open the Contact page
 *
 * @param navigate - React Router navigate function
 * @param navigationStarted - Optional callback to trigger loading state
 */
export const navigateToContact = (
  navigate: (path: string) => void,
  navigationStarted?: (destination?: string) => void
): void => {
  scrollToTopAndNavigate(navigate, '/contact', navigationStarted);
};
